import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
  SelectionProviderChildProps
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import {
  DatabaseTablesByIdQuery,
  DatabaseTablesByIdQuery_databases_tablesConnection_nodes,
  DatabaseTablesByIdQueryVariables,
} from '../../../gen-src/queries/DatabaseTablesByIdQuery';
import { TablesDisplayGridRowDataFragment } from '../../../gen-src/queries/TablesDisplayGridRowDataFragment';
import { DatabaseTableSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatabaseTablesByIdQueryGql } from '../../queries/DatabaseTablesByIdQueryGql';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { getRowDataWithMaxCountsForTable } from '../table/functions/TableRowDataFunctions';
import { TablesDisplayGridRowData } from '../table/types/TablesDisplayGridRowData';
import { DatabaseTablesDisplayGrid } from './DatabaseTablesDisplayGrid';

type QueryVariables = DatabaseTablesByIdQueryVariables;
type QueryResults = DatabaseTablesByIdQuery;
type QueryResultListNode = DatabaseTablesByIdQuery_databases_tablesConnection_nodes;
type GridRowData = TablesDisplayGridRowData;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].tablesConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.databases && resultData.databases[0];
}

function getRowDataForTable(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResult(resultData));
}

export type DatabaseTablesQueryGridProps =
  SelectionProviderChildProps &
  GridSortProps<TablesValidSortFields> & {
    readonly databaseId: string;
    readonly rowCount: number | null;
  };

export function DatabaseTablesQueryGrid(props: DatabaseTablesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  return (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={DatabaseTablesByIdQueryGql}
      partialQueryVariables={{
        databaseId: props.databaseId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForTable}
    >
      {({ rowStore }) => (
        <DatabaseTablesDisplayGrid
          rowStore={rowStore}
          selectedKeys={props.selectedKeys}
          updateSelectionForKey={props.updateSelectionForKey}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      )}
    </PaginatedQueryWrapper>
  );
}
